import type { ReactNode } from "react";

import { Over } from "./Hero";

/**
 * One bar for every ratio (SNIPPETS §15): steps vs goal, In vs budget, level XP, protein.
 * An 8px track in a 16px SVG, a fill, and an optional 2×16 target tick.
 *
 * `max` is the scale. `target` puts a tick at that value (a goal inside a longer scale). With
 * `ceiling`, `max` is a limit: past it the fill turns danger at 100%, the tick moves to where the
 * limit falls on the longer bar, and the word "over" is added to `detail` — never colour alone.
 * `value: null` draws an empty track (not measured is not zero, invariant 11).
 *
 * `partial` marks a total that is a lower bound (SNIPPETS §31): under the bar, a dashed "≥" pill
 * and the words that name what is missing ("Mango pickle not known"). The fill is then only the
 * known part, which is what `value` already is; the caller puts "≥ " before the figure.
 */
export function ProgressBar({
  value,
  max,
  target,
  ceiling,
  label,
  detail,
  overText,
  partial,
  ariaLabel,
}: {
  value: number | null;
  max: number;
  target?: number;
  ceiling?: boolean;
  /** left of the head row, 15/600: "6,412 steps" */
  label?: ReactNode;
  /** right of the head row, 13.8 muted: "goal 8,000", "budget ~1,810" */
  detail?: ReactNode;
  /** replaces the automatic "240 over" */
  overText?: ReactNode;
  /** the words under a lower-bound bar, after the "≥" pill */
  partial?: ReactNode;
  ariaLabel: string;
}) {
  const v = value ?? 0;
  const over = !!ceiling && value !== null && max > 0 && v > max;
  const scale = over ? v : max;
  const pct = (n: number) => `${scale > 0 ? Math.max(0, Math.min(100, (n / scale) * 100)).toFixed(1) : 0}%`;
  const tickAt = over ? max : target;

  const overWords = over ? (overText ?? `${Math.round(v - max).toLocaleString()} over`) : null;
  const right =
    detail !== undefined || overWords ? (
      <span className="k-pbar__detail">
        {detail}
        {detail !== undefined && overWords ? " · " : null}
        {overWords ? <Over>{overWords}</Over> : null}
      </span>
    ) : null;

  return (
    <div className={`k-pbar${over ? " k-pbar--over" : ""}`}>
      {label !== undefined || right ? (
        <div className="k-pbar__head">
          <span className="k-pbar__label">{label}</span>
          {right}
        </div>
      ) : null}
      <svg role="img" aria-label={ariaLabel}>
        <rect className="k-pbar__track" x="0" y="4" width="100%" height="8" rx="4" />
        {value !== null && v > 0 ? (
          <rect className="k-pbar__fill" x="0" y="4" width={over ? "100%" : pct(v)} height="8" rx="4" />
        ) : null}
        {tickAt !== undefined && tickAt > 0 ? (
          <rect className="k-pbar__tick" x={pct(tickAt)} y="0" width="2" height="16" rx="1" />
        ) : null}
      </svg>
      {partial ? (
        // Hidden from assistive tech: the bar's own label already says it in words.
        <div className="k-pbar__partial" aria-hidden="true">
          <span className="k-pbar__pill">
            ≥
          </span>
          <span>{partial}</span>
        </div>
      ) : null}
    </div>
  );
}
